'use client';

/** @jsxImportSource @emotion/react */
import { type AnimationEvent, useLayoutEffect, useRef, useState } from 'react';

import { HeaderTopSection } from '../HeaderTopSection/HeaderTopSection';
import { useStyles } from '../HeaderTopSection/HeaderTopSection.styles';
import Navbar from '../navbar/Navbar';

type HeaderProps = {
  showNavbar?: boolean;
  disableScrollEffect?: boolean;
};

const SCROLL_ENTER_THRESHOLD = 120;
const SCROLL_EXIT_THRESHOLD = 60;
const DESKTOP_BREAKPOINT = 1058;
const STICKY_ANIMATION_DURATION = 500;

const Header = ({ showNavbar = true, disableScrollEffect = false }: HeaderProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [isEnteringSticky, setIsEnteringSticky] = useState(false);
  const [isReturningToTop, setIsReturningToTop] = useState(false);

  const headerRef = useRef<HTMLElement | null>(null);
  const wasScrolledRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const headerFlowHeightRef = useRef<number | null>(null);
  const scrollFrameRef = useRef<number | null>(null);
  const animationResetTimeoutRef = useRef<number | null>(null);

  const styles = useStyles(scrolled);

  useLayoutEffect(() => {
    const header = headerRef.current;

    if (!header || !showNavbar) {
      header?.style.removeProperty('--landing-header-flow-height');
      headerFlowHeightRef.current = null;
      return;
    }

    const updateHeaderFlowHeight = () => {
      if (window.innerWidth <= DESKTOP_BREAKPOINT) {
        header.style.removeProperty('--landing-header-flow-height');
        return;
      }

      if (wasScrolledRef.current) {
        if (headerFlowHeightRef.current !== null) {
          header.style.setProperty(
            '--landing-header-flow-height',
            `${headerFlowHeightRef.current}px`,
          );
        }

        return;
      }

      const height = header.getBoundingClientRect().height;

      if (height <= 0) {
        return;
      }

      const flowHeight = Math.ceil(height);

      headerFlowHeightRef.current = flowHeight;
      header.style.setProperty('--landing-header-flow-height', `${flowHeight}px`);
    };

    updateHeaderFlowHeight();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateHeaderFlowHeight) : null;

    resizeObserver?.observe(header);

    window.addEventListener('resize', updateHeaderFlowHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateHeaderFlowHeight);
    };
  }, [showNavbar]);

  useLayoutEffect(() => {
    if (disableScrollEffect) {
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = null;
      }

      if (animationResetTimeoutRef.current !== null) {
        window.clearTimeout(animationResetTimeoutRef.current);
        animationResetTimeoutRef.current = null;
      }

      hasInitializedRef.current = false;
      wasScrolledRef.current = false;
      setScrolled(false);
      setIsEnteringSticky(false);
      setIsReturningToTop(false);
      return;
    }

    const updateScrollState = () => {
      const currentScrollY = window.scrollY;
      const nextScrolled = wasScrolledRef.current
        ? currentScrollY > SCROLL_EXIT_THRESHOLD
        : currentScrollY > SCROLL_ENTER_THRESHOLD;

      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        wasScrolledRef.current = nextScrolled;
        setScrolled(nextScrolled);
        return;
      }

      if (wasScrolledRef.current === nextScrolled) {
        return;
      }

      if (!wasScrolledRef.current && nextScrolled) {
        setIsReturningToTop(false);
        setIsEnteringSticky(true);
      }

      if (wasScrolledRef.current && !nextScrolled) {
        setIsEnteringSticky(false);
        setIsReturningToTop(true);
      }

      wasScrolledRef.current = nextScrolled;
      setScrolled(nextScrolled);

      if (animationResetTimeoutRef.current !== null) {
        window.clearTimeout(animationResetTimeoutRef.current);
      }

      animationResetTimeoutRef.current = window.setTimeout(() => {
        setIsEnteringSticky(false);
        setIsReturningToTop(false);
        animationResetTimeoutRef.current = null;
      }, STICKY_ANIMATION_DURATION);
    };

    const handleScroll = () => {
      if (scrollFrameRef.current !== null) {
        return;
      }

      scrollFrameRef.current = window.requestAnimationFrame(() => {
        scrollFrameRef.current = null;
        updateScrollState();
      });
    };

    updateScrollState();

    window.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);

      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = null;
      }

      if (animationResetTimeoutRef.current !== null) {
        window.clearTimeout(animationResetTimeoutRef.current);
        animationResetTimeoutRef.current = null;
      }
    };
  }, [disableScrollEffect]);

  const handleAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.currentTarget !== event.target) {
      return;
    }

    if (animationResetTimeoutRef.current !== null) {
      window.clearTimeout(animationResetTimeoutRef.current);
      animationResetTimeoutRef.current = null;
    }

    setIsEnteringSticky(false);
    setIsReturningToTop(false);
  };

  const headerClassName = [
    scrolled ? 'is-scrolled' : '',
    isEnteringSticky ? 'entering-sticky' : '',
    isReturningToTop ? 'returning-to-top' : '',
    !showNavbar ? 'without-navbar' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <header ref={headerRef} css={styles.fullHeaderWrapper} className={headerClassName}>
      <div data-header-surface css={styles.headerSurface} onAnimationEnd={handleAnimationEnd}>
        <HeaderTopSection scrolled={scrolled} showMobileMenu={showNavbar} />

        {showNavbar && (
          <div css={styles.desktopNavbarWrapper}>
            <Navbar scrolled={scrolled} animateSticky={isEnteringSticky} />
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
